import { useMemo, useState } from 'react';
import { AddButton, LiveScreen, QueryState, formatDate, type Column } from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { FarmerPhoto, usePhotoUrls } from '@/components/FarmerPhoto';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { StakeholderDrawer, STAKEHOLDER_TYPES, relationLabel, sideLabel, type StakeholderRow } from '@/screens/parts/StakeholderDrawer';

/**
 * Every farm's stakeholders in one list (owner, 2026-09-21: "a separate list of stakeholders") -
 * neighbours, officials, path users, buyers - with how to reach them and when they were last
 * consulted, which is what an FSC or EUDR auditor asks to see. Geography and role scope apply as
 * on every other register.
 */
export function StakeholderList({ farmerId }: { farmerId?: string }) {
  const { profile } = useAuth();
  const [stat, setStat] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<StakeholderRow | 'new' | null>(null);
  const mayAdd = can(profile, 'party.register', 'create');

  const rows = useRows<StakeholderRow>('farm_stakeholders', {
    filters: { farmer_id: farmerId },
    search: search ? { columns: ['full_name', 'organisation_name', 'phone', 'farmer_name', 'stakeholder_code'], term: search } : undefined,
    orderBy: { column: 'created_at', ascending: false },
  });
  const photos = usePhotoUrls((rows.data ?? []).map((row) => row.photo_document_id));

  const STATS = useMemo(() => [
    { key: 'all', label: 'Stakeholders', match: () => true, tone: 'neutral' as const },
    ...(farmerId ? [] : [
      { key: 'org', label: 'Organisation level', tone: 'info' as const, match: (row: StakeholderRow) => row.stakeholder_level === 'organisation' },
      { key: 'farm', label: 'Farmer level', tone: 'info' as const, match: (row: StakeholderRow) => row.stakeholder_level !== 'organisation' },
    ]),
    { key: 'indigenous', label: 'Indigenous', tone: 'warn' as const, match: (row: StakeholderRow) => row.is_indigenous === true },
    ...STAKEHOLDER_TYPES.map((type) => ({
      key: type.id, label: type.label, tone: 'info' as const, match: (row: StakeholderRow) => row.stakeholder_type === type.id,
    })),
    // The plot record used to carry its own compass-ordered list of neighbours; it was taken out
    // on 2026-09-25 ("can have stakeholders also seperate page, and remove from plot"), so what
    // it showed has to be reachable from here. Filter to Neighbours, sort by Plot, read the Side.
    { key: 'neighbour', label: 'Neighbours', tone: 'info' as const, match: (row: StakeholderRow) => row.relation_to_farm === 'adjoining_land' },
    { key: 'row', label: 'Right of way', tone: 'warn' as const, match: (row: StakeholderRow) => row.relation_to_farm === 'right_of_way' },
    { key: 'never', label: 'Never consulted', tone: 'error' as const, match: (row: StakeholderRow) => !row.last_consulted_on },
  ], [farmerId]);
  const visible = useMemo(() => applyStat(rows.data ?? [], STATS, stat), [rows.data, STATS, stat]);

  const columns: Array<Column<StakeholderRow>> = useMemo(() => [
    { key: 'photo', label: '', render: (row) => (
      <FarmerPhoto name={row.full_name} size={32} url={row.photo_document_id ? photos.data?.[row.photo_document_id] : null} />
    ) },
    { key: 'full_name', label: 'Stakeholder', render: (row) => (
      <div><strong>{row.full_name}</strong>{row.organisation_name && <div className="muted">{row.organisation_name}</div>}</div>
    ) },
    { key: 'stakeholder_type', label: 'Type', render: (row) => STAKEHOLDER_TYPES.find((type) => type.id === row.stakeholder_type)?.label ?? row.stakeholder_type },
    { key: 'role_label', label: 'Role', render: (row) => row.role_label ?? '--' },
    { key: 'relation_to_farm', label: 'Relation to farm', render: (row) => relationLabel(row.relation_to_farm) },
    ...(farmerId ? [] : [{ key: 'farmer_name', label: 'Farm (farmer)', render: (row: StakeholderRow) => (row.farmer_name ? `${row.farmer_name} · ${row.farmer_code}` : 'Organisation level') }]),
    { key: 'plot_code', label: 'Plot', render: (row) => row.plot_code ?? (row.farmer_id ? 'Whole farm' : '--') },
    // Which side of the plot their land lies on. Only a neighbour has one, and without it a list
    // of neighbours cannot be walked in order round a boundary - which is what it is for.
    { key: 'boundary_side', label: 'Side', render: (row) => (row.boundary_side ? sideLabel(row.boundary_side) : '--') },
    { key: 'is_indigenous', label: 'Indigenous', render: (row) => (row.is_indigenous == null ? '--' : row.is_indigenous ? `Yes${row.indigenous_community ? ` (${row.indigenous_community})` : ''}` : 'No') },
    { key: 'phone', label: 'Phone', render: (row) => row.phone ?? '--' },
    { key: 'email', label: 'Email', render: (row) => row.email ?? '--' },
    { key: 'last_consulted_on', label: 'Last consulted', render: (row) => (row.last_consulted_on ? formatDate(row.last_consulted_on) : 'Never') },
  ], [photos.data, farmerId]);

  return (
    <>
      {farmerId && (
        <div className="toolbar-row">
          <span className="muted">People and bodies with a stake in this farm.</span>
          <button className="primary" disabled={!mayAdd} onClick={() => setOpen('new')} type="button">Add stakeholder</button>
        </div>
      )}
      <StatCards active={stat} onSelect={setStat} rows={rows.data ?? []} stats={STATS} />
      <QueryState empty="No stakeholders recorded yet." query={rows}>
        <DataGrid columns={columns} exportName="stakeholders" onRowClick={(row) => setOpen(row)}
                  onSearchChange={setSearch} rows={visible} screenKey="party.register" search={search}
                  searchPlaceholder="Name, organisation, phone or farmer" />
      </QueryState>
      {!farmerId && (
        <AddButton disabled={!mayAdd} label="Add stakeholder" onClick={() => setOpen('new')}
                   title={mayAdd ? undefined : 'Your role cannot add stakeholders.'} />
      )}
      {open && (
        <StakeholderDrawer editing={open === 'new' ? null : open} farmerId={farmerId}
                           key={open === 'new' ? 'new' : open.id} onClose={() => setOpen(null)} open />
      )}
    </>
  );
}

export default function StakeholdersScreen() {
  return (
    <LiveScreen subtitle="At organisation level (forest office, panchayat, NGOs, buyers) and at farmer level (neighbours, path users) - with when each was last consulted." title="Stakeholders">
      <StakeholderList />
    </LiveScreen>
  );
}
